// Vendors
import React, { useContext, useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

// Components
import { Trans, Form } from '../../..';
import { ButtonPrimary, Button } from 'texkit/dist/components/Buttons';
import PaymentSelectCardList from '../../PaymentSelectCardList/PaymentSelectCardList';

// Actions
import { goBack } from 'connected-react-router';
import {
  clearErrorData,
  submitPaymentFromForm,
  submitPaymentFromAccountList,
} from '../../../../reducers/payment/paymentActions';
import { PaymentContext } from '../../Payment';

// Helpers
import { createCvvSchema, paymentFormSchema } from '../../../../lib/validation';
import { APP_CONSTANTS } from '../../../../lib/data';

// Selectors
import { selectPaymentState } from '../../../../reducers/payment/paymentSelectors';
import { selectPathname } from '../../../../reducers/route/routeSelectors';

const PaymentType = ({ paymentState: { paymentInfo = {} } }) => {
  if (paymentInfo.feeInfo) {
    return (
      <h3 className="payment-select-account-title">
        <Trans file="Labels" id="RenewalFee" fallback="Renewal fee" />
      </h3>
    );
  }
  return <p hidden>This hidden content makes the heading accessible.</p>;
};

const SelectPaymentAccount = () => {
  const { t: translate } = useTranslation('tpwd');
  const dispatch = useDispatch();
  const [fdmsCardData, setFdmsCardData] = useState({});
  const [walletCardData, setWalletCardData] = useState({});
  const [isAddCard, setIsAddCard] = useState(false);
  const paymentState = useSelector(selectPaymentState);
  const currentPath = useSelector(selectPathname);
  const paymentContext = useContext(PaymentContext);
  const [isAddCardFormFilled, setIsAddCardFormFilled] = useState(false);
  const [isAddCardFormValid, setIsAddCardFormValid] = useState(false);

  const onSubmit = async (values, formikActions) => {
    await dispatch(clearErrorData());

    if (!isAddCard && Object.keys(values.selectPayment).length) {
      return dispatch(
        submitPaymentFromAccountList(
          values,
          formikActions,
          paymentContext,
          translate
        )
      );
    }

    try {
      // Revalidate the cvv field with the current card data before submitting
      paymentFormSchema.validateSyncAt(
        'cvv',
        { cvv: values.cvv },
        { context: fdmsCardData }
      );

      const paymentPayload = {
        nameOnCard: values.name,
        cardNumber: fdmsCardData.token ? fdmsCardData.token.slice(-4) : '',
        paymentToken: fdmsCardData.token || '',
        paymentAccountType:
          APP_CONSTANTS.CARD_TYPES[fdmsCardData.cardtype] || '',
        expirationDate: values.expirationDate.replace('/', ''),
        cvv: values.cvv,
        saveSelectedCard: !!values.savePaymentAccount,
        address: {
          address1: values.streetAddress,
          address2: values.streetAddress2 || '',
          city: values.city,
          // changing to uppercase to match backend requirement, sometimes state is passed in lowercase as the user types it in
          state: (values.state || '').toUpperCase(),
          postalCode: values.zip,
          countryId: '1',
          country: 'USA',
        },
      };

      await dispatch(
        submitPaymentFromForm(
          paymentContext,
          paymentPayload,
          formikActions,
          translate
        )
      );
    } catch (error) {
      if (error.name === 'ValidationError') {
        formikActions.setFieldError('cvv', error.message);
      } else {
        console.error('Error submitting payment form', error);
      }
    } finally {
      formikActions.setSubmitting(false);
    }
  };

  useEffect(
    () => () => {
      dispatch(clearErrorData());
    },
    [dispatch]
  );

  const validationSchema = useMemo(() => {
    const paymentUrls = APP_CONSTANTS.AGENCY_PAYMENT_URLS;
    // Only use this cvv validation if the user is using the wallet
    const useCvvSchema =
      !isAddCard &&
      paymentUrls.includes(currentPath) &&
      walletCardData.cvvRequired;

    if (useCvvSchema) {
      const cardtype = walletCardData.cardType;
      return createCvvSchema(cardtype);
    }

    return {};
  }, [currentPath, isAddCard, walletCardData]);

  const handleSelectCardChange = (paymentAccount, formikProps) => {
    formikProps.setTouched({});
    formikProps.setErrors({});
    const isWalletSelection = Boolean(paymentAccount);
    setIsAddCard(!isWalletSelection);
    setWalletCardData(isWalletSelection ? paymentAccount : {});

    if (isWalletSelection) {
      setFdmsCardData({});
    }
  };

  return (
    <div className="payment-select-account">
      <h3 className="payment-select-account-title">
        <PaymentType paymentState={paymentState} />
      </h3>
      <h3>
        <Trans file="Labels" id="PaymentMethod" />
      </h3>

      <Form
        className="payment-select-account-form"
        formikConfig={{
          onSubmit,
          initialValues: { selectPayment: '' },
          validationSchema,
        }}
        render={({ FormError, FormActions, FormActionsItem, formikProps }) => {
          const { isSubmitting, isValid: isWalletCvvValid } = formikProps;

          const isSubmitDisabled =
            (isAddCard && !fdmsCardData.token) ||
            (isAddCard && !isAddCardFormFilled) ||
            (isAddCard && !isAddCardFormValid) ||
            (!isAddCard && !isWalletCvvValid) ||
            isSubmitting;

          return (
            <>
              <PaymentSelectCardList
                formikProps={formikProps}
                onChange={paymentAccount =>
                  handleSelectCardChange(paymentAccount, formikProps)
                }
                onCardFieldMessage={fdmsMessage => setFdmsCardData(fdmsMessage)}
                onFormError={errors => {
                  const hasErrors = Object.keys(errors).length > 0;
                  setIsAddCardFormValid(!hasErrors);
                }}
                onFormFilled={isFilled => {
                  setIsAddCardFormFilled(isFilled);
                }}
              />
              <FormError />
              <FormActions>
                <FormActionsItem>
                  <ButtonPrimary
                    type="submit"
                    isLoading={isSubmitting}
                    disabled={isSubmitDisabled}
                    loadingContent={<Trans file="Labels" id="Submitting" />}
                  >
                    <Trans file="Payment" id="PayNow" fallback="Pay Now" />
                  </ButtonPrimary>
                </FormActionsItem>
                <FormActionsItem>
                  <Button
                    className="payment-button-outline"
                    onClick={() => dispatch(goBack())}
                    disabled={isSubmitting}
                  >
                    <Trans file="Labels" id="Back" fallback="Back" />
                  </Button>
                </FormActionsItem>
              </FormActions>
            </>
          );
        }}
      />
    </div>
  );
};

PaymentType.propTypes = {
  paymentState: PropTypes.shape({
    paymentInfo: PropTypes.object,
  }).isRequired,
};

export default SelectPaymentAccount;
